import { useCallback, useState } from 'react';
import List from '@mui/material/List';
import { useTranslation } from 'react-i18next';
import { FilterSection } from 'common/components/Filters/FilterSection';
import { FiltersDialog, FiltersDialogProps } from 'common/components/Filters/FiltersDialog';
import { FilterListItem } from 'common/components/Filters/FilterListItem';
import Box from '@mui/material/Box';
import { FilterBox } from 'common/components/Filters/FilterBox';
import { Actions, Subjects } from 'permissions/ability';
import { SimInventoryCan } from 'permissions/PermissionProvider';
import { RadioSection } from 'common/components/Filters/IdFilterSelector/RadioSection';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useQuery } from '@tanstack/react-query';
import { simProductsFiltersAtom } from 'atoms/filters/sim-products';
import { SimProductsIdSearchInput } from './Inputs/SimProductsIdSearchInput';
import { IccidRangeSearch } from './Inputs/IccidRangeSearchInput';
import { SessionStatusInput } from './Inputs/SessionStatusInput';
import { AccountSearchInput } from './Inputs/AccountSearchInput';
import { ProductCodesSearchInput } from './Inputs/ProductCodesSearchInput';
import { MnoSearchInput } from './Inputs/MNOSearchInput';
import { ConnectionIdSearchInput } from './Inputs/ConnectionIdSearchInput';
import { OrderNumberSearchInput } from './Inputs/OrderNumberSearchInput';
import { SimProductCustomFieldInputs } from './Inputs/CutomFieldInputs';
import { TagsSearchInput } from './Inputs/TagsSearchInput';
import { customFieldsQuery } from 'admin/customFields/utils/queries';
import { SimProductsSuscriptionStatusInput } from './Inputs/SimProductsSuscriptionStatusInput';

type SimFiltersDialogProps = Pick<FiltersDialogProps, 'onCancel' | 'onClear' | 'onClose'> & {
  onApply: (filters: any) => void;
};

export const SimProductsFiltersDialog = ({ onApply, onClear, ...props }: SimFiltersDialogProps) => {
  const { t } = useTranslation();

  const filters = useAtomValue(simProductsFiltersAtom);
  const resetFilters = useResetAtom(simProductsFiltersAtom);

  const [selectedSearch, setSelectedSearch] = useState<'search' | 'iccidRange'>(() => {
    if (filters.iccidFrom !== '' || filters.iccidTo !== '') {
      return 'iccidRange';
    }

    return 'search';
  });

  const onClearClicked = useCallback(() => {
    resetFilters();
    onClear();
  }, []);

  const onApplyClicked = useCallback(() => {
    if (Number.parseInt(filters.iccidFrom) > Number.parseInt(filters.iccidTo)) {
      onApply({ ...filters, iccidFrom: '', iccidTo: '' });
    } else {
      onApply(filters);
    }
  }, [filters]);

  const { data: customFieldsData } = useQuery<any>({
    queryKey: customFieldsQuery.all,
    enabled: false, //It will use previous fetched custom fields
  });

  const customFieldsAvailable = customFieldsData?.length > 0;

  return (
    <FiltersDialog
      {...props}
      open={true}
      onApply={onApplyClicked}
      onClear={onClearClicked}
      oneColumn={!customFieldsAvailable}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
        <FilterSection title={t('common.idSearch')} role="region" aria-label={t('common.idSearch')}>
          <FilterBox>
            <RadioSection
              options={['search', 'iccidRange']}
              value={selectedSearch}
              onValueChange={(newValue) => setSelectedSearch(newValue as 'search' | 'iccidRange')}
              labels={{
                search: 'Search',
                iccidRange: 'ICCID Range',
              }}
            >
              {(value) => {
                return (
                  <Box>
                    <Box
                      data-selector="search"
                      sx={{ opacity: value === 'search' ? 1 : 0 }}
                      aria-hidden={value !== 'search'}
                    >
                      <SimProductsIdSearchInput />
                    </Box>
                    <Box
                      data-selector="iccidRange"
                      sx={{ opacity: value === 'iccidRange' ? 1 : 0 }}
                      aria-hidden={value !== 'iccidRange'}
                    >
                      <IccidRangeSearch />
                    </Box>
                  </Box>
                );
              }}
            </RadioSection>
          </FilterBox>
        </FilterSection>
        <FilterSection title={t('simInventory.sim')} data-testid="simSection">
          <List>
            <FilterListItem
              label={t('simInventory.subscriptionStatus')}
              htmlFor="subscriptionStatus"
            >
              <SimProductsSuscriptionStatusInput />
            </FilterListItem>
            <FilterListItem label={t('simInventory.sessionStatus')} htmlFor="select status">
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <SessionStatusInput />
              </Box>
            </FilterListItem>
            <SimInventoryCan I={Actions.filter} a={Subjects.internalData}>
              <FilterListItem label="Account" htmlFor="account">
                <AccountSearchInput />
              </FilterListItem>
            </SimInventoryCan>
            <FilterListItem label="Product Code" htmlFor="productCode">
              <ProductCodesSearchInput />
            </FilterListItem>
            <FilterListItem
              label={t('simInventory.mobileNetworkOperator')}
              htmlFor="mobileNetworkOperator"
            >
              <MnoSearchInput />
            </FilterListItem>
            <FilterListItem label={'Tag'} htmlFor="tag">
              <TagsSearchInput />
            </FilterListItem>
          </List>
        </FilterSection>
        <FilterSection title={t('common.order')}>
          <List>
            <FilterListItem label={t('common.connectionId')} htmlFor="connectionId">
              <ConnectionIdSearchInput />
            </FilterListItem>
            <FilterListItem label={t('simInventory.orderNumber')} htmlFor="orderNumber">
              <OrderNumberSearchInput />
            </FilterListItem>
          </List>
        </FilterSection>
      </Box>
      {customFieldsAvailable && (
        <FilterSection title={t('customFields.customFields')}>
          <List>
            <SimProductCustomFieldInputs />
          </List>
        </FilterSection>
      )}
    </FiltersDialog>
  );
};
